import { useState } from 'react';
import { Check, X } from 'lucide-react';
import { useFormClients, type FormClientRow } from '@/api/hooks';

export interface SelectedClient {
  clientId: string;
  clientName: string;
  clientFullName: string;
  terminated?: boolean;
}

const LETTERS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

/**
 * Multi-select client picker using the app's standard client search ("Client starts
 * with" + "Search (Lookup Client Name)"). Results are server-side (top 50), click a
 * row to add or remove, chips show the selection. scope 'active' = existing clients
 * (termination); 'all' = existing + terminated (reactivation).
 */
export function MultiClientPicker({
  scope, value, onChange, disabled, single,
}: {
  scope: 'active' | 'all';
  value: SelectedClient[];
  onChange: (v: SelectedClient[]) => void;
  disabled?: boolean;
  single?: boolean; // pick exactly one client (Discontinuation)
}) {
  const [grouping, setGrouping] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const results = useFormClients(scope, search, grouping);
  const selectedIds = new Set(value.map((c) => c.clientId));
  const searched = search.trim().length >= 2 || grouping.length > 0;

  function toggle(r: FormClientRow) {
    const picked = { clientId: r.ClientID, clientName: r.ClientName, clientFullName: r.ClientFullName, terminated: !!r.Terminated };
    if (selectedIds.has(r.ClientID)) {
      onChange(value.filter((c) => c.clientId !== r.ClientID));
    } else {
      onChange(single ? [picked] : [...value, picked]);
    }
  }

  return (
    <div className="space-y-2">
      {/* Selected chips */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((c) => (
            <span key={c.clientId} className="inline-flex items-center gap-1 rounded bg-secondary px-2 py-0.5 text-xs">
              {c.clientFullName}
              {c.terminated ? <span className="text-accent">(terminated)</span> : null}
              {!disabled && (
                <button type="button" aria-label="Remove" className="text-muted-foreground hover:text-primary" onClick={() => onChange(value.filter((x) => x.clientId !== c.clientId))}>
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Search controls (matches the Clients/Engagements search) */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="label">Client starts with</label>
          <select className="input w-32" value={grouping} disabled={disabled} onChange={(e) => setGrouping(e.target.value)}>
            <option value="">All</option>
            {LETTERS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div className="min-w-[220px] flex-1">
          <label className="label">Search (Lookup Client Name)</label>
          <div className="flex gap-2">
            <input
              className="input"
              placeholder="Client name…"
              value={searchInput}
              disabled={disabled}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && setSearch(searchInput)}
            />
            <button className="btn-secondary" type="button" disabled={disabled} onClick={() => setSearch(searchInput)}>Search</button>
          </div>
        </div>
      </div>

      {/* Results */}
      {searched && (
        <div className="max-h-64 overflow-auto rounded-md border border-border">
          {results.isFetching && <div className="px-3 py-2 text-sm text-muted-foreground">Searching…</div>}
          {!results.isFetching && (results.data?.length ?? 0) === 0 && (
            <div className="px-3 py-2 text-sm text-muted-foreground">No matches. Try a different name or letter.</div>
          )}
          {(results.data ?? []).map((r) => {
            const on = selectedIds.has(r.ClientID);
            return (
              <button
                key={r.ClientID}
                type="button"
                disabled={disabled}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-secondary/60 ${on ? 'bg-secondary/40' : ''}`}
                onClick={() => toggle(r)}
              >
                <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${on ? 'border-primary bg-primary text-primary-foreground' : 'border-border'}`}>
                  {on && <Check className="h-3 w-3" />}
                </span>
                <span>{r.ClientFullName}{r.Terminated ? <span className="ml-1 text-accent">(terminated)</span> : null}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
